import { useState } from "react";
import type { IconRef, SentenceToken } from "../game/types";
import { iconKey } from "../game/types";
import Frame from "./Frame";
import IconZoom from "./IconZoom";
import { getCell } from "../data/boards";
import { useTranslation } from "react-i18next";
import type { Lang } from "../i18n";

interface Props {
  cellRef: IconRef;
  token?: SentenceToken;
  onClick?: (ref: IconRef) => void;
  disabled?: boolean;
  showSolutionGloss?: boolean;
}

export default function IconCell({ cellRef, token, onClick, disabled, showSolutionGloss }: Props) {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage as Lang) ?? "en";
  const [imgError, setImgError] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const key = iconKey(cellRef);
  const src = `/boards/board-${cellRef.board}-${cellRef.col}${cellRef.row}.png`;
  const cell = getCell(cellRef.board, cellRef.col, cellRef.row);
  const fallbackText = cell?.gloss?.[lang] ?? cell?.gloss?.en ?? key;

  function handleClick() {
    if (disabled) return;
    if (onClick) onClick(cellRef);
    else setZoomed(true);
  }

  return (
    <div
      className={`icon-cell${disabled ? " disabled" : ""}`}
      data-icon={key}
      onClick={handleClick}
    >
      {/* No gloss tooltip/alt — hovering an icon must NOT reveal its word, or it
          spoils the puzzle. The meaning is shown only on click-to-zoom (gated)
          and on the post-game reveal overlay below. */}
      {!imgError ? (
        <img src={src} alt={key} onError={() => setImgError(true)} loading="lazy" />
      ) : (
        <span style={{ fontSize: 11, color: "#1a1d2e", padding: 4, textAlign: "center", lineHeight: 1.1, fontWeight: 700 }}>
          {key}
        </span>
      )}
      {token && <Frame token={token} />}
      {showSolutionGloss && !token && (
        <span style={{ position: "absolute", inset: 0, background: "rgba(20,24,42,0.6)", display: "grid", placeItems: "center", color: "white", fontSize: 9, fontWeight: 700, padding: 4, textAlign: "center" }}>
          {fallbackText}
        </span>
      )}
      {zoomed && <IconZoom cellRef={cellRef} token={token} onClose={() => setZoomed(false)} />}
    </div>
  );
}
